// client/src/cognitive-flows/components/canvas/markdownList.js
//
// List behaviour for the text block's editor. Pure string functions so the
// behaviour is testable without a DOM — the textarea wrapper only supplies the
// value and the caret position.
//
// Bullets already RENDERED before this existed (the markdown renderer handles
// `-` lists); what was missing was writing them without typing the syntax on
// every line, which is most of what a dashboard note actually is.

const BULLET_RE = /^(\s*)([-*])(\s+)(.*)$/;
const ORDERED_RE = /^(\s*)(\d+)([.)])(\s+)(.*)$/;

/** The [start, end] character offsets of the lines the selection touches. */
export function lineRangeAt(value, selectionStart, selectionEnd = selectionStart) {
    const start = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const nextBreak = value.indexOf('\n', selectionEnd);
    return [start, nextBreak === -1 ? value.length : nextBreak];
}

/**
 * Enter inside a list continues it: `- a` ⏎ gives you `- ` on the next line,
 * and `1. a` gives `2. `.
 *
 * Pressing Enter on an EMPTY list item ends the list instead of adding another
 * empty bullet — that is how every editor people already use behaves, and
 * without it there is no way out of a list except deleting the marker by hand.
 *
 * Returns `{ value, caret }`, or null when the caret isn't in a list and the
 * textarea should just do its default thing.
 */
export function continueListOnEnter(value, caret) {
    const lineStart = value.lastIndexOf('\n', caret - 1) + 1;
    const line = value.slice(lineStart, caret);

    const bullet = BULLET_RE.exec(line);
    if (bullet) {
        const [, indent, marker, gap, text] = bullet;
        if (text.trim() === '') {
            // Empty item: drop the marker and leave the list.
            const next = `${value.slice(0, lineStart)}${value.slice(caret)}`;
            return { value: next, caret: lineStart };
        }
        const insert = `\n${indent}${marker}${gap}`;
        return { value: value.slice(0, caret) + insert + value.slice(caret), caret: caret + insert.length };
    }

    const ordered = ORDERED_RE.exec(line);
    if (ordered) {
        const [, indent, num, dot, gap, text] = ordered;
        if (text.trim() === '') {
            const next = `${value.slice(0, lineStart)}${value.slice(caret)}`;
            return { value: next, caret: lineStart };
        }
        const insert = `\n${indent}${Number(num) + 1}${dot}${gap}`;
        return { value: value.slice(0, caret) + insert + value.slice(caret), caret: caret + insert.length };
    }

    return null;
}

/**
 * Toggle `- ` on every line the selection touches. Adds bullets unless EVERY
 * touched line already has one, in which case it strips them — so the same
 * button both makes and unmakes a list, and a mixed selection becomes fully
 * bulleted rather than half-toggled.
 */
export function toggleBullets(value, selectionStart, selectionEnd) {
    const [from, to] = lineRangeAt(value, selectionStart, selectionEnd);
    const lines = value.slice(from, to).split('\n');
    const meaningful = lines.filter((l) => l.trim() !== '');
    const allBulleted = meaningful.length > 0 && meaningful.every((l) => BULLET_RE.test(l));

    const next = lines.map((line) => {
        if (line.trim() === '') return line;
        if (allBulleted) return line.replace(BULLET_RE, '$1$4');
        // Strip any marker the line already has before adding one: on a mixed
        // selection this is what stops an already-bulleted line becoming
        // "- - one".
        return `- ${line.replace(BULLET_RE, '$4').replace(/^\s+/, '')}`;
    }).join('\n');

    return {
        value: value.slice(0, from) + next + value.slice(to),
        // Select the whole reformatted block so the button can be pressed
        // twice in a row without reselecting.
        selectionStart: from,
        selectionEnd: from + next.length,
    };
}
